import { Fragment, useEffect, useState } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { supabase, type UserProfile } from '../../../lib/supabase';
import { Link, useNavigate } from 'react-router-dom';
import { Package, Briefcase, LogOut, User } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { useAuth } from '../../../context/AuthContext';

const ADMIN_ID = '92040e94-abd9-4dd2-971f-a1be769d4f11';

export function UserProfileMenu() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const navigate = useNavigate();
  const isAdmin = profile?.role === 'admin' || user?.id === ADMIN_ID;

  useEffect(() => {
    if (!user?.id) {
      setProfile(null);
      return;
    }
    supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single()
      .then(({ data, error }) => {
        if (!error && data) setProfile(data);
        else setProfile(null);
      });
  }, [user?.id]);

  const handleSignOut = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    try {
      await supabase.auth.signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (!user) {
    return null;
  }

  const firstName = profile?.first_name || user.email?.split('@')[0] || 'Utilisateur';
  const avatarUrl = profile?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(firstName)}&background=ff0442&color=fff&size=128`;

  return (
    <Menu as="div" className="relative inline-block text-left">
      <div>
        <Menu.Button className="group relative inline-flex items-center gap-2 rounded-xl bg-white/10 backdrop-blur-sm px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-white/30 transition-all duration-200 hover:bg-white/20 hover:shadow-md hover:ring-white/50 focus:outline-none focus:ring-2 focus:ring-white/50 focus:ring-offset-2 focus:ring-offset-[#ff0442]">
          <img
            src={avatarUrl}
            alt={firstName}
            className="h-8 w-8 rounded-full object-cover ring-2 ring-white/30"
          />
          <span className="hidden font-medium sm:inline">{firstName}</span>
          <ChevronDownIcon className="h-4 w-4 text-white/80 transition-transform duration-200 group-hover:text-white ui-open:rotate-180" aria-hidden="true" />
        </Menu.Button>
      </div>

      <Transition
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="transform opacity-0 scale-95 translate-y-1"
        enterTo="transform opacity-100 scale-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="transform opacity-100 scale-100 translate-y-0"
        leaveTo="transform opacity-0 scale-95 translate-y-1"
      >
        <Menu.Items className="absolute right-0 z-50 mt-2 w-56 origin-top-right rounded-2xl bg-white/95 backdrop-blur-xl shadow-xl ring-1 ring-neutral-200/50 focus:outline-none dark:bg-neutral-800/95 dark:ring-neutral-700/50">
          <div className="p-2">
            {/* Header avec profil */}
            <div className="px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
              <div className="flex items-center gap-3">
                <img
                  src={avatarUrl}
                  alt={firstName}
                  className="h-10 w-10 rounded-full object-cover ring-2 ring-neutral-200 dark:ring-neutral-700"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                    {firstName} {profile?.last_name || ''}
                  </p>
                  <p className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                    {user.email}
                  </p>
                </div>
              </div>
            </div>

            {/* Menu items */}
            <div className="py-1">
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/mon-profil"
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <User className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      {t('account.my_profile')}
                    </span>
                  </Link>
                )}
              </Menu.Item>
              <Menu.Item>
                {({ active }) => (
                  <Link
                    to="/mes-commandes"
                    className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                      active
                        ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                        : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                    }`}
                  >
                    <Package className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                    <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                      {t('account.my_orders')}
                    </span>
                  </Link>
                )}
              </Menu.Item>
              {isAdmin && (
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/espace-business"
                      className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 ${
                        active
                          ? 'bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20'
                          : 'hover:bg-neutral-50 dark:hover:bg-neutral-700/50'
                      }`}
                    >
                      <Briefcase className="h-5 w-5 text-neutral-600 dark:text-neutral-400" />
                      <span className="text-sm font-medium text-neutral-800 dark:text-neutral-100">
                        {t('account.business')}
                      </span>
                    </Link>
                  )}
                </Menu.Item>
              )}
            </div>

            {/* Séparateur */}
            <div className="border-t border-neutral-200 dark:border-neutral-700 my-1" />

            {/* Déconnexion — Menu.Item as="button" pour que le clic déclenche bien signOut */}
            <Menu.Item as="button" type="button" onClick={handleSignOut}>
              {({ active }) => (
                <span
                  className={`group relative flex w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-all duration-200 cursor-pointer ${
                    active
                      ? 'bg-red-50 dark:bg-red-900/20'
                      : 'hover:bg-red-50 dark:hover:bg-red-900/20'
                  }`}
                >
                  <LogOut className="h-5 w-5 text-red-600 dark:text-red-400 shrink-0" />
                  <span className="text-sm font-medium text-red-600 dark:text-red-400">
                    {t('account.sign_out')}
                  </span>
                </span>
              )}
            </Menu.Item>
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

